import React, { useCallback, useEffect, useState } from 'react';
import type {
  GitHubConnectionAck,
  UserSettingsPublic,
  UserSettingsUpdate,
} from '../../services/clients/types';
import { gitHubClient } from '../../services/clients';
import { Button } from '../../ui/common/Button';
import { SettingsConnectionStatus } from './SettingsConnectionStatus';
import { ProviderIcon } from './ProviderIcon';
import { ProviderSetupGuideLink } from './ProviderSetupGuideModal';
import { ConnectionTestLabel } from './ConnectionTestLabel';
import { errorMessage, githubAckChipLabel, githubAckChipOk } from './settingsHelpers';
import { buttonRowStyle, hintStyle, inputStyle, sectionStyle } from './settingsStyles';

export interface SettingsGitHubSectionProps {
  inAccordion?: boolean;
  settings: UserSettingsPublic;
  saving: boolean;
  githubTokenDraft: string;
  onGithubTokenDraft: (value: string) => void;
  onSavePatch: (
    patch: UserSettingsUpdate,
    successMessage: string,
    after?: () => void,
  ) => Promise<void>;
}

export const SettingsGitHubSection: React.FC<SettingsGitHubSectionProps> = ({
  settings,
  saving,
  githubTokenDraft,
  onGithubTokenDraft,
  onSavePatch,
  inAccordion = false,
}) => {
  const status = settings.secrets.githubPersonalAccessToken;
  const hasDraft = Boolean(githubTokenDraft.trim());
  const connected = Boolean(status?.configured);
  const [ack, setAck] = useState<GitHubConnectionAck | null>(null);
  const [ackBusy, setAckBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const loadAck = useCallback(async () => {
    try {
      const envelope = await gitHubClient.getConnection();
      setAck((prev) => {
        // Keep probe-verified login or sticky probe errors until the next test/save/clear.
        if (prev?.login) return prev;
        if (prev?.status === 'error') return prev;
        return envelope.data;
      });
    } catch {
      // Keep sticky probe state when presence GET fails.
    }
  }, []);

  useEffect(() => {
    void loadAck();
  }, [loadAck, status?.configured]);

  const runTest = useCallback(async () => {
    setAckBusy(true);
    setLocalError(null);
    try {
      const envelope = await gitHubClient.testConnection();
      setAck(envelope.data);
      if (envelope.data.status === 'error') {
        setLocalError(envelope.data.error || 'No se pudo verificar la conexión con GitHub.');
      }
    } catch (err) {
      setLocalError(errorMessage(err, 'No se pudo verificar la conexión con GitHub.'));
    } finally {
      setAckBusy(false);
    }
  }, []);

  return (
    <section
      style={{ ...sectionStyle, borderBottom: 'none' }}
      data-testid="settings-github-section"
    >
      <SettingsConnectionStatus
        testIdPrefix="settings-github"
        title="GitHub"
        chipLabel={githubAckChipLabel(ack, connected, status?.lastFour ?? null)}
        chipOk={githubAckChipOk(ack, connected)}
        hint="Usa un GitHub PAT con los permisos mínimos necesarios para repositorios, pull requests, checks y reviews. Se guarda solo en el servidor."
        icon={<ProviderIcon name="github" />}
        headerAction={
          !inAccordion ? (
            <Button
              size="sm"
              variant="ghost"
              data-testid="settings-github-test-connection"
              disabled={saving || ackBusy || !connected}
              loading={ackBusy}
              onClick={() => void runTest()}
            >
              <ConnectionTestLabel />
            </Button>
          ) : undefined
        }
        compact={inAccordion}
      >
        {localError ? (
          <p
            data-testid="settings-github-local-error"
            style={{ ...hintStyle, color: 'var(--z-danger)' }}
          >
            {localError}
          </p>
        ) : null}

        <input
          data-testid="settings-github-token"
          type="password"
          autoComplete="off"
          placeholder={
            status?.configured
              ? 'Pega un nuevo GitHub PAT para reemplazarlo'
              : 'Pega tu GitHub PAT para conectar la cuenta'
          }
          value={githubTokenDraft}
          onChange={(e) => onGithubTokenDraft(e.target.value)}
          style={inputStyle}
        />
        <ProviderSetupGuideLink source="github" />
        <div style={buttonRowStyle}>
          {hasDraft ? (
            <Button
              size="sm"
              variant="primary"
              data-testid="settings-github-save"
              disabled={saving}
              loading={saving}
              onClick={() =>
                void onSavePatch(
                  { githubPersonalAccessToken: githubTokenDraft.trim() },
                  'Token de GitHub guardado. Usa Probar conexión para verificarlo.',
                  () => {
                    onGithubTokenDraft('');
                    void runTest();
                  },
                )
              }
            >
              Guardar token
            </Button>
          ) : null}
          {connected ? (
            <Button
              size="sm"
              variant="ghost"
              data-testid="settings-github-clear"
              disabled={saving}
              onClick={() =>
                void onSavePatch(
                  { clearGithubPersonalAccessToken: true },
                  'Token de GitHub eliminado',
                  () => {
                    onGithubTokenDraft('');
                    setAck(null);
                  },
                )
              }
            >
              Eliminar token
            </Button>
          ) : null}
        </div>
      </SettingsConnectionStatus>
    </section>
  );
};
